import React from 'react';
import { Typography, Space, Row, Col, Card } from 'antd';
import { 
  CloudUploadOutlined, 
  ThunderboltOutlined, 
  CheckCircleOutlined,
  BarChartOutlined,
  UnlockOutlined,
  SettingOutlined
} from '@ant-design/icons';
import SEO from '../components/SEO';

const { Title, Text, Paragraph } = Typography;

const HowItWorks = () => {
  return (
    <div className="w-full max-w-6xl mx-auto px-6 py-12 md:py-24 animate-fade-in-up">
      <SEO 
        title="How It Works - COMPRESSAI Engine"
        description="Learn how COMPRESS AI optimizes your images with extreme precision, batch ZIP support, and high-performance telemetry."
        keywords={['how image compression works', 'compressai engine specs', 'lossless compression technology', 'batch optimization workflow']}
      />
      
      <div className="text-center mb-16">
        <Title level={1} className="!text-white !text-5xl md:!text-6xl !font-bold tracking-tight mb-4 text-glow">
          COMPRESSAI <span className="text-purple-400">ENGINE v1.0</span>
        </Title>
        <Text className="text-purple-200/60 text-lg md:text-xl font-medium tracking-wide max-w-2xl block mx-auto">
          The proprietary algorithm behind the world's fastest image optimization workflow.
        </Text>
      </div>

      <Row gutter={[32, 48]} className="mb-24">
        <Col xs={24} md={8}>
          <Card className="glass-panel-dark border-white/5 h-full hover:border-purple-500/30 transition-all duration-500 group">
            <div className="w-14 h-14 bg-purple-500/20 rounded-2xl flex items-center justify-center mb-6 border border-purple-500/30 group-hover:bg-purple-500/30 transition-all duration-300">
              <CloudUploadOutlined className="text-purple-400 text-3xl" />
            </div>
            <Title level={3} className="!text-white !font-bold mb-4">1. Batch Upload</Title>
            <Paragraph className="text-slate-400 text-base leading-relaxed">
              Upload your heavy assets in a single .ZIP file up to 200MB. Our engine is optimized for bulk processing, handling hundreds of images in seconds.
            </Paragraph>
          </Card>
        </Col>
        
        <Col xs={24} md={8}>
          <Card className="glass-panel-dark border-white/5 h-full hover:border-indigo-500/30 transition-all duration-500 group">
            <div className="w-14 h-14 bg-indigo-500/20 rounded-2xl flex items-center justify-center mb-6 border border-indigo-500/30 group-hover:bg-indigo-500/30 transition-all duration-300">
              <ThunderboltOutlined className="text-indigo-400 text-3xl" />
            </div>
            <Title level={3} className="!text-white !font-bold mb-4">2. Deep Analysis</Title>
            <Paragraph className="text-slate-400 text-base leading-relaxed">
              COMPRESSAI Engine performs real-time telemetry analysis, identifying color patterns and redundant pixels without compromising clarity.
            </Paragraph>
          </Card>
        </Col>
        
        <Col xs={24} md={8}>
          <Card className="glass-panel-dark border-white/5 h-full hover:border-emerald-500/30 transition-all duration-500 group">
            <div className="w-14 h-14 bg-emerald-500/20 rounded-2xl flex items-center justify-center mb-6 border border-emerald-500/30 group-hover:bg-emerald-500/30 transition-all duration-300">
              <CheckCircleOutlined className="text-emerald-400 text-3xl" />
            </div>
            <Title level={3} className="!text-white !font-bold mb-4">3. Optimization</Title>
            <Paragraph className="text-slate-400 text-base leading-relaxed">
              Achieve up to 81%+ reduction in file size. Download your optimized results instantly with full transparency on bandwidth savings.
            </Paragraph>
          </Card>
        </Col>
      </Row>

      <div className="glass-panel-dark border-white/5 p-8 md:p-12 rounded-[2rem] overflow-hidden relative group">
        <div className="absolute top-0 right-0 w-[40%] h-[40%] bg-purple-600/10 blur-[100px] rounded-full z-0 pointer-events-none group-hover:bg-purple-600/20 transition-all duration-500"></div>
        
        <Row gutter={[48, 48]} align="middle">
          <Col xs={24} lg={12}>
            <Title level={2} className="!text-white !font-bold mb-6">Real-Time Telemetry</Title>
            <Space direction="vertical" size="large" className="w-full">
              <div className="flex items-start gap-4">
                <BarChartOutlined className="text-purple-400 text-2xl mt-1" />
                <div>
                  <Title level={4} className="!text-white !m-0">Precise Metrics</Title>
                  <Text className="text-slate-400">Track raw size, optimized size, and exact savings ratio for every payload.</Text>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <UnlockOutlined className="text-indigo-400 text-2xl mt-1" />
                <div>
                  <Title level={4} className="!text-white !m-0">Enterprise Security</Title>
                  <Text className="text-slate-400">All processing is protected by enterprise-grade encryption for maximum privacy.</Text>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <SettingOutlined className="text-emerald-400 text-2xl mt-1" />
                <div>
                  <Title level={4} className="!text-white !m-0">Automated Scalability</Title>
                  <Text className="text-slate-400">Infrastructure built with extreme precision for high-performance enterprise workflows.</Text>
                </div>
              </div>
            </Space>
          </Col>
          <Col xs={24} lg={12}>
            <div className="relative rounded-2xl overflow-hidden glass-panel-dark border-white/10 shadow-2xl p-4 transform hover:scale-[1.02] transition-transform duration-500">
              <div className="absolute top-4 left-4 flex gap-2">
                <div className="w-3 h-3 rounded-full bg-red-500/50"></div>
                <div className="w-3 h-3 rounded-full bg-amber-500/50"></div>
                <div className="w-3 h-3 rounded-full bg-emerald-500/50"></div>
              </div>
              <div className="bg-slate-900/50 p-6 rounded-xl mt-4 border border-white/5">
                <div className="flex justify-between items-center mb-6">
                  <Text className="text-purple-400 font-bold uppercase tracking-widest text-[10px]">Processing Telemetry</Text>
                  <div className="px-2 py-0.5 bg-purple-500/10 rounded-full border border-purple-500/20">
                    <Text className="text-purple-400 text-[10px] uppercase font-bold">Live</Text>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="h-2 w-full bg-slate-800 rounded-full overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 w-[81%] shadow-[0_0_15px_rgba(168,85,247,0.5)]"></div>
                  </div>
                  <div className="flex justify-between">
                    <Text className="text-slate-500 text-xs">Compression Efficiency</Text>
                    <Text className="text-purple-400 font-bold text-xs uppercase tracking-tighter">81% Reduction</Text>
                  </div>
                </div>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default HowItWorks;
